// src/components/global/Table/TableLayout.jsx
import React from 'react';
import { Box, Paper } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import TableHeader from './TableHeader';
import TableFilterBar from './TableFilterBar';
import TableBulkToolbar from './TableBulkToolbar';
import TableBody from './TableBody';
import TableMenu from './TableMenu';

const TableLayout = (props) => {
    const { i18n } = useTranslation();
    const lng = i18n.language || 'en';
    const isArabic = lng === 'ar';
    const theme = useTheme();

    const {
        title,
        subtitle,
        search,
        onSearchChange,
        addButtonLabel,
        onAddClick,
        addButtonVisible = true,
        isLoading,
        error,
        items,
        meta,
        page,
        perPage,
        onPageChange,
        onPerPageChange,
        sortBy,
        sortDir,
        onRequestSort,
        selectedIds = [],
        onSelectAllClick,
        onRowCheckboxClick,
        onBulkDelete,
        onBulkRestore,
        isBulkDeleting = false,
        isBulkRestoring = false,
        columns,
        renderRow,
        menuAnchorEl,
        openMenu,
        onMenuOpen,
        onMenuClose,
        onMenuView,
        onMenuEdit,
        onMenuRestore,
        onMenuDelete,
        onMenuConfirm,
        onMenuCloseAction,
        onMenuExport,
        addButtonDisabled = false,
        addButtonTooltip,
        isDeletingSingle,
        menuDisableEdit = false,
        menuDisableRestore = false,
        menuDisableDelete = false,
        menuDisableConfirm = false,
        menuDisableClose = false,
        menuDisableExport = false,
        bulkDeleteDisabled,
        bulkRestoreDisabled,
        emptyLabelAr = 'لا توجد بيانات',
        emptyLabelEn = 'No data',
        loadErrorAr = 'حدث خطأ أثناء تحميل البيانات',
        loadErrorEn = 'Failed to load data',
        filters,
        onClearAllFilters,
    } = props;

    const enhancedRenderRow = (params) =>
        renderRow({
            ...params,
            selectedIds,
            onRowCheckboxClick,
            onMenuOpen,
        });

    return (
        <Box
            sx={{
                mt: 3,
                direction: isArabic ? 'rtl' : 'ltr',
            }}
        >
            <Paper
                elevation={0}
                sx={{
                    mb: 2,
                    borderRadius: 2.5,
                    overflow: 'hidden',
                    border: (t) => `1px solid ${t.palette.divider}`,
                    boxShadow: theme.palette.boxShadow,
                    bgcolor: theme.palette.background.card,
                }}
            >
                <TableHeader
                    title={title}
                    subtitle={subtitle}
                    search={search}
                    onSearchChange={onSearchChange}
                    addButtonLabel={addButtonLabel}
                    onAddClick={onAddClick}
                    addButtonDisabled={addButtonDisabled}
                    addButtonTooltip={addButtonTooltip}
                    addButtonVisible={addButtonVisible}
                />

                {filters && filters.length > 0 && (
                    <TableFilterBar
                        filters={filters}
                        onClearAll={onClearAllFilters}
                    />
                )}

                <TableBulkToolbar
                    selectedCount={selectedIds?.length || 0}
                    onBulkDelete={onBulkDelete}
                    onBulkRestore={onBulkRestore}
                    isBulkDeleting={isBulkDeleting}
                    isBulkRestoring={isBulkRestoring}
                    bulkDeleteDisabled={bulkDeleteDisabled}
                    bulkRestoreDisabled={bulkRestoreDisabled}
                />

                <TableBody
                    isLoading={isLoading}
                    error={error}
                    items={items}
                    meta={meta}
                    page={page}
                    perPage={perPage}
                    onPageChange={onPageChange}
                    onPerPageChange={onPerPageChange}
                    sortBy={sortBy}
                    sortDir={sortDir}
                    onRequestSort={onRequestSort}
                    selectedIds={selectedIds}
                    onSelectAllClick={onSelectAllClick}
                    columns={columns}
                    renderRow={enhancedRenderRow}
                    emptyLabelAr={emptyLabelAr}
                    emptyLabelEn={emptyLabelEn}
                    loadErrorAr={loadErrorAr}
                    loadErrorEn={loadErrorEn}
                />

                <TableMenu
                    anchorEl={menuAnchorEl}
                    open={openMenu}
                    onClose={onMenuClose}
                    onView={onMenuView}
                    onEdit={onMenuEdit}
                    onRestore={onMenuRestore}
                    onDelete={onMenuDelete}
                    onConfirm={onMenuConfirm}
                    onCloseAction={onMenuCloseAction}
                    onExport={onMenuExport}
                    disableEdit={menuDisableEdit}
                    disableRestore={menuDisableRestore}
                    disableDelete={menuDisableDelete}
                    disableConfirm={menuDisableConfirm}
                    disableClose={menuDisableClose}
                    disableExport={menuDisableExport}
                    isDeletingSingle={isDeletingSingle}
                />
            </Paper>
        </Box>
    );
};

export default TableLayout;
